import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { authApi, handleApiError } from '../lib/api-client';
import { ILoginResponseDto, User, UserRole } from '../types/api';

export const useRegister = () => {
  return useMutation({
    mutationFn: authApi.register,
    onSuccess: data => {
      toast.success('Registration successful! Please wait for admin approval.');
    },
    onError: handleApiError,
  });
};

export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.login,
    onSuccess: data => {
      localStorage.setItem('access_token', data.access_token);

      const userData: ILoginResponseDto = data;

      localStorage.setItem('user', JSON.stringify(userData));
      queryClient.setQueryData(['currentUser'], userData);

      toast.success('Login successful!');
      if (data.user?.mustChangePassword) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    },
    onError: handleApiError,
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    queryClient.clear();
    navigate('/login');
    toast.success('Logged out successfully');
  };
};

export const getCurrentUser = (): User | null => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  try {
    const stored = JSON.parse(userStr) as any;

    // Support multiple payload shapes observed in storage:
    // - { access_token, user: { success, data: {...} } }
    // - { user: {...} }
    // - {...user}
    const userContainer = stored?.user ?? stored;
    const userData = userContainer?.data ?? userContainer;
    if (!userData) return null;

    // Backend RBAC sends `role` as an object: { id, name, code, permissions, facilityTypes, ... }.
    // Legacy login may send `role` as a string (e.g. "ADMIN").
    const roleField = userData.role;
    const roleFromField =
      typeof roleField === 'string'
        ? roleField
        : roleField && typeof roleField === 'object'
          ? (roleField as any).code ?? (roleField as any).name
          : undefined;

    const roleFromRolesArray =
      Array.isArray(userData.roles) && userData.roles.length > 0 ? userData.roles[0]?.code : undefined;

    const role = roleFromField ?? roleFromRolesArray;

    const assignedRole =
      roleField && typeof roleField === 'object'
        ? {
            id: (roleField as any).id ?? '',
            name: (roleField as any).name ?? '',
            code: (roleField as any).code ?? '',
            description: (roleField as any).description ?? '',
            isSystem: Boolean((roleField as any).isSystem),
            createdAt: (roleField as any).createdAt ?? '',
            updatedAt: (roleField as any).updatedAt ?? '',
            permissions: Array.isArray((roleField as any).permissions)
              ? (roleField as any).permissions.map((p: any) => ({
                  id: p?.id ?? '',
                  code: p?.code ?? '',
                  name: p?.name ?? '',
                  description: p?.description,
                }))
              : [],
            facilityTypes: Array.isArray((roleField as any).facilityTypes)
              ? (roleField as any).facilityTypes.map((ft: any) => ({
                  id: ft?.id ?? '',
                  name: ft?.name ?? '',
                  nameKinyarwanda: ft?.nameKinyarwanda ?? '',
                  nameFrench: ft?.nameFrench ?? '',
                  code: ft?.code ?? '',
                  type: ft?.type ?? '',
                  responsibleMinistries: Array.isArray(ft?.responsibleMinistries) ? ft.responsibleMinistries : [],
                  createdAt: ft?.createdAt ?? '',
                  updatedAt: ft?.updatedAt ?? '',
                }))
              : [],
          }
        : null;

    // Extract RBAC permission codes + facility type IDs from the role object (new backend).
    // If role isn't an object, we fall back to legacy behavior (no codes).
    const permissionCodes: string[] =
      assignedRole && Array.isArray(assignedRole.permissions)
        ? assignedRole.permissions.map((p: any) => p?.code).filter((c: any) => typeof c === 'string')
        : [];

    const allowedFacilityTypeIds: string[] =
      assignedRole && Array.isArray(assignedRole.facilityTypes)
        ? assignedRole.facilityTypes.map((ft: any) => ft?.id).filter((c: any) => typeof c === 'string')
        : [];

    return {
      ...(userData as User),
      role: role as UserRole | undefined,
      permissionCodes: permissionCodes.length ? permissionCodes : undefined,
      allowedFacilityTypeIds: allowedFacilityTypeIds.length ? allowedFacilityTypeIds : undefined,
      assignedRole,
    };
  } catch {
    // Corrupted/legacy local storage should not crash the app shell.
    return null;
  }
};

export const isAuthenticated = () => {
  return !!localStorage.getItem('access_token');
};
